import { addMonths } from "date-fns";
import { useQueries, useQuery } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import { AppUser, useAppContext } from "../AppContext";
import useQueryParams from "./useQueryParams";
import { is_appointment, is_rounds, is_template_time } from "../EventFilters";
import { overlayStudentAvailabilityWithSpecialtyClinicAvailability, parseEventsToStudentAvailability, ClinicBlockAvailability, ClinicBlockAvailabilityForDates, overlayStudentAvailabilityWithUnavailibility } from "../common/ParseEventsToAvailability";
import { DataIntegrityReport, ExcelClinicInfo } from "../common/types";
import { ClinicsEnum } from "../common/enums";
import { cloneDeep } from "lodash";
import { sanatizeDirtyClinicStringToEnum } from "../common/utils";

type CalendarParams = {
  onyen: string;
};

export default function useClinicCalendar(usePrivateCalendar: boolean, specialtyClinicData?: ExcelClinicInfo[], selectedSpecialtyClinic: ClinicsEnum = ClinicsEnum.HOME) {
  const app = useAppContext();
  const { onyen } = useParams<CalendarParams>();

  const queryParams = useQueryParams();
  const additionalOnyens = queryParams.get("additionalOnyens")?.split(',') || []

  const nowDate = new Date()
  let query = new URLSearchParams(useLocation().search)

  const startDate = query.get('queryStartDate') ? new Date(query.get('queryStartDate')!) : nowDate;
  const endDate = query.get('queryEndDate') ? new Date(query.get('queryEndDate')!) : addMonths(nowDate, 6);

  const userId = onyen || query.get('userId') || app.user?.email?.split("@")[0];;
  const { data: userInfo } = useQuery<{ data: AppUser | undefined }>(`user-details?userId=${userId?.split(".")[0]}`, {
    enabled: !!userId && !usePrivateCalendar,
  })

  const userQueries = useQueries(
    [userId, ...additionalOnyens].map(user => {
      return {
        queryKey: `user-calendar?userId=${user}`,
        enabled: !!user && !usePrivateCalendar,
      }
    })
  )

  const userEvents = cloneDeep(userQueries.map(query => query.data).flat()[0]) as {[key: string]: ClinicBlockAvailability[]};
  const specialtyClinicInfo = specialtyClinicData?.find(sc => sanatizeDirtyClinicStringToEnum(sc.clinicName) === selectedSpecialtyClinic);
 
  const isDoingFirstLoad = userQueries.some(query => query.isLoading);
  const isLoadingData = userQueries.some(query => query.isFetching);

  const iscalendarAccessable = (app.events!==undefined)
  const isEpicScheduleSlotsAccessable = (app.events?.some(is_template_time)) || false
  const isEpicRoundsAccessable = (app.events?.some(is_rounds)) || false
  const isEpicAppointmentsAccessable = (app.events?.some(is_appointment)) || true
  const isOnboarded = iscalendarAccessable && isEpicScheduleSlotsAccessable && isEpicRoundsAccessable && isEpicAppointmentsAccessable

  let dataFeedWarning : DataIntegrityReport | undefined = undefined
  let availabilityInfoForEachDay: ClinicBlockAvailabilityForDates = usePrivateCalendar ? parseEventsToStudentAvailability(app.events ||[]) : new Map(Object.entries(userEvents ?? {}));

  //Display warnings to users if data is suspected missing
  if (specialtyClinicInfo===undefined && isLoadingData!==true) {
    dataFeedWarning = {
      URL : "Unknown",
      accessedSuccessfully:true, 
      errorSummary:"No excel sheets found matching this clinic. Showing home availability.", 
      possiblyHumanInterperableAmbiguities:[]};
  }else{
    //use data from parser
    dataFeedWarning = specialtyClinicInfo?.dataIntegrityReport
  }

  if(specialtyClinicInfo?.datesAndChairs.length !==0 && specialtyClinicInfo?.datesAndState.length===0 ){
    availabilityInfoForEachDay = overlayStudentAvailabilityWithSpecialtyClinicAvailability(availabilityInfoForEachDay, specialtyClinicInfo)
  }else if(specialtyClinicInfo?.datesAndState.length !==0 && specialtyClinicInfo?.datesAndChairs.length===0 ){
    availabilityInfoForEachDay = overlayStudentAvailabilityWithUnavailibility(availabilityInfoForEachDay, specialtyClinicInfo)
  }

  return {
    onyen,
    availabilityInfoForEachDay,
    anyWarningAboutDataFeedsUsed: dataFeedWarning,
    isDoingFirstLoad,
    isLoading: isLoadingData,
    userInfo,
    startDate,
    endDate,
    isOnboarded,
    iscalendarAccessable,
    isEpicScheduleSlotsAccessable,
    isEpicRoundsAccessable,
    isEpicAppointmentsAccessable,
  }
}